import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import Modal from "../../../components/Modal/Modal"
import { getAllSectorAssociationRepresentatives } from "../service/api"
import {
  getMosaListWithUsers,
  attachCompaniesToSAR,
  detachCompaniesFromSAR,
  Mosa,
} from "../service/mosa-api"
import { getAllPlantsList } from "../../companies/service/api"
import { User } from "../types"
import { Company } from "../../companies/types"

export const ManageSARCompanies: React.FC = () => {
  const { t } = useTranslation()
  const [sarUsers, setSarUsers] = useState<User[]>([])
  const [mosaList, setMosaList] = useState<Mosa[]>([])
  const [allCompanies, setAllCompanies] = useState<Company[]>([])
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [selectedUser, setSelectedUser] = useState<User | null>(null)
  const [selectedCompanyIds, setSelectedCompanyIds] = useState<number[]>([])

  const loadData = async () => {
    try {
      setLoading(true)
      const [usersResponse, mosasResponse, companiesResponse] = await Promise.all([
        getAllSectorAssociationRepresentatives({}),
        getMosaListWithUsers({}),
        getAllPlantsList(),
      ])
      setSarUsers(usersResponse.data || [])
      setMosaList(mosasResponse.data || [])
      setAllCompanies(companiesResponse.data || [])
    } catch (error) {
      console.error("Error loading data:", error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadData()
  }, [])

  const getUserMosa = (user: User): Mosa | undefined => {
    return mosaList.find((mosa) =>
      mosa.users?.some((u) => u.id === user.id)
    )
  }

  const handleAttachUnattach = (user: User) => {
    setSelectedUser(user)
    const userCompanyIds = user.companies?.map((c) => c.id) || []
    setSelectedCompanyIds(userCompanyIds)
    setShowModal(true)
  }

  const toggleCompany = (companyId: number) => {
    setSelectedCompanyIds((prev) =>
      prev.includes(companyId)
        ? prev.filter((id) => id !== companyId)
        : [...prev, companyId]
    )
  }

  const handleSave = async () => {
    if (!selectedUser) return

    const userMosa = getUserMosa(selectedUser)
    if (!userMosa) {
      alert("This SAR user is not assigned to any Mosa entity")
      return
    }

    try {
      const currentCompanyIds = selectedUser.companies?.map((c) => c.id) || []
      const companiesToAttach = selectedCompanyIds.filter(
        (id) => !currentCompanyIds.includes(id)
      )
      const companiesToDetach = currentCompanyIds.filter(
        (id) => !selectedCompanyIds.includes(id)
      )

      if (companiesToAttach.length > 0) {
        await attachCompaniesToSAR({
          userId: parseInt(selectedUser.id),
          companyIds: companiesToAttach,
          mosaId: userMosa.id,
        })
      }

      if (companiesToDetach.length > 0) {
        await detachCompaniesFromSAR({
          userId: parseInt(selectedUser.id),
          companyIds: companiesToDetach,
        })
      }

      setShowModal(false)
      await loadData()
    } catch (error) {
      console.error("Error updating companies:", error)
      alert("Error updating companies. Please try again.")
    }
  }

  if (loading) {
    return (
      <div className="d-flex justify-content-center p-5">
        <div className="spinner-border" role="status">
          <span className="sr-only">Loading...</span>
        </div>
      </div>
    )
  }

  return (
    <div className="manage-sar-companies">

      <table className="table table-light table-bordered">
        <thead>
          <tr>
            <th>{t("Name")}</th>
            <th>{t("Sector Association")}</th>
            <th>{t("Plants")}</th>
            <th>{t("Action")}</th>
          </tr>
        </thead>
        <tbody>
          {sarUsers.map((user) => {
            const userMosa = getUserMosa(user)
            return (
              <tr key={user.id}>
                <td>{user.name}</td>
                <td>{userMosa?.name || t("Not assigned")}</td>
                <td>
                  {user.companies && user.companies.length > 0 ? (
                    <ul className="mb-0">
                      {user.companies.map((company) => (
                        <li key={company.id}>{company.name}</li>
                      ))}
                    </ul>
                  ) : (
                    <span>{t("No plants assigned")}</span>
                  )}
                </td>
                <td>
                  <button
                    className="btn btn-sm"
                    style={{ backgroundColor: "#74BE0F", color: "white" }}
                    onClick={() => handleAttachUnattach(user)}
                  >
                    {t("Attach/Unattach Plant(s)")}
                  </button>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>

      {showModal && selectedUser && (
        <Modal isOpen={showModal} isOpenHandler={setShowModal} wrapperClass="srs-modal-lg">
          <div className="p-5">
            <h2>{t("Manage Plants for")} {selectedUser.name}</h2>

            <div className="mt-4">
              <p>{t("Select companies to attach to this SAR")}:</p>
              <div style={{ maxHeight: "400px", overflowY: "auto" }}>
                {allCompanies.map((company) => (
                  <div key={company.id} className="form-check">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id={`company-${company.id}`}
                      checked={selectedCompanyIds.includes(company.id)}
                      onChange={() => toggleCompany(company.id)}
                    />
                    <label className="form-check-label" htmlFor={`company-${company.id}`}>
                      {company.name} ({company.country?.name || "N/A"})
                    </label>
                  </div>
                ))}
              </div>
            </div>

            <div className="d-flex justify-content-end mt-4">
              <button
                className="btn btn-secondary mr-2"
                onClick={() => setShowModal(false)}
              >
                {t("Cancel")}
              </button>
              <button
                className="btn"
                style={{ backgroundColor: "#74BE0F", color: "white" }}
                onClick={handleSave}
              >
                {t("Save")}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  )
}
